import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRelativeTime } from '@/utils/date';
import { DEFS } from '@/canvas/defs/index';
import { useCohort } from '@/components/canvas/cohortData';
import { ProgressBar } from '@/components/canvas/bits';
import PackView, { IndexStrip } from '@/components/canvas/PackView';
import { buildPackModel, downloadPackPdf } from '@/components/canvas/packTools';
import { StatusSquares } from '@/components/canvas/StatusSquares';

export const route = { path: '/cohort/:id', layout: 'owner', access: "profile === 'Facilitator_c'" };

export default function CohortLearner() {
  const { id } = useParams();
  const { learners, loading, error, reload } = useCohort();
  const [busy, setBusy] = useState(false);

  const learner = useMemo(() => learners.find((l) => String(l.id) === String(id)) || null, [learners, id]);
  const model = useMemo(
    () =>
      learner
        ? buildPackModel({ states: learner.states, agg: learner.agg, learner: { name: learner.name, email: '', organisation: learner.organisation } })
        : null,
    [learner],
  );

  const back = (
    <Button variant="ghost" size="sm" asChild>
      <Link to="/cohort">
        <ApperIcon name="ArrowLeft" size={16} /> Back to Cohort
      </Link>
    </Button>
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6" aria-busy="true">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-96 w-full rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        {back}
        <div className="space-y-4 rounded-2xl border border-destructive/40 bg-destructive/10 p-6">
          <h1 className="font-heading text-xl font-semibold">This learner did not load</h1>
          <p className="text-sm">{error}</p>
          <Button onClick={reload}>Try again</Button>
        </div>
      </div>
    );
  }

  if (!learner || !model) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        {back}
        <div className="space-y-2 rounded-2xl border border-border bg-card p-6">
          <h1 className="font-heading text-xl font-semibold">Learner not found</h1>
          <p className="text-sm text-muted-foreground">They may not have saved a canvas yet. Go back to the cohort list and pick someone else.</p>
        </div>
      </div>
    );
  }

  async function download() {
    setBusy(true);
    try {
      await downloadPackPdf(model, { name: learner.name }, { onlyConfirmed: !learner.ready });
      toast.success('The PDF is downloading.');
    } catch {
      toast.error('The PDF could not be made. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      {back}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-heading text-3xl font-semibold text-balance">{learner.name}</h1>
          <p className="text-muted-foreground">
            {learner.organisation || 'Organisation not given'}
            {learner.lastChange ? `. Last change ${formatRelativeTime(learner.lastChange)}.` : ''}
          </p>
        </div>
        <Button size="lg" onClick={download} disabled={busy || learner.confirmed === 0}>
          {busy ? (
            <>
              <ApperIcon name="LoaderCircle" size={18} className="animate-spin" /> Making the PDF
            </>
          ) : (
            <>
              <ApperIcon name="FileDown" size={18} /> {learner.ready ? 'Download PDF' : 'Download draft PDF'}
            </>
          )}
        </Button>
      </div>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-base font-semibold">Progress</h2>
          <span className="text-sm text-muted-foreground">
            {learner.confirmed} of {DEFS.length} confirmed{learner.stale > 0 ? `, ${learner.stale} to review` : ''}
          </span>
        </div>
        <ProgressBar value={learner.overall} label={`${learner.name} progress`} />
        <StatusSquares states={learner.states} />
        {!learner.ready && <p className="text-xs text-muted-foreground">The PDF holds the confirmed canvases only until all ten are confirmed.</p>}
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-lg font-semibold">Their pack</h2>
        <IndexStrip model={model} />
        <PackView model={model} />
      </section>
    </div>
  );
}
